import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Space, Select, Modal, message, Tooltip, Alert } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave,
  faClock,
  faCircleCheck,
  faPaperPlane,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { usePageHeader } from '../../../layouts/AdminLayout';
import OnDemandPayoutService from '../../../services/OnDemandPayoutService';
import {
  PageContainer,
  TopBar,
  ActionButton,
  StyledTable,
  StatusTag,
  EmptyState,
  StatsRow,
  StatCard,
} from '../StorageUnits/styled';

const STATUS_META = {
  pending_review: { label: 'In Review', color: 'gold' },
  approved: { label: 'Approved', color: 'blue' },
  processing: { label: 'Processing', color: 'geekblue' },
  paid: { label: 'Paid', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
  canceled: { label: 'Canceled', color: 'default' },
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'processing', label: 'Processing' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
];

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const PayoutsPage = () => {
  const { setPageHeader } = usePageHeader();

  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [actioningId, setActioningId] = useState(null);
  const [balance, setBalance] = useState(null);
  const [balanceError, setBalanceError] = useState(null);

  useEffect(() => {
    setPageHeader({ title: 'Payouts', count: payouts.length, loading });
  }, [setPageHeader, payouts.length, loading]);

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OnDemandPayoutService.getPayouts({
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      if (response.success) {
        setPayouts(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching payouts:', error);
      message.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  const fetchBalance = useCallback(async () => {
    try {
      const response = await OnDemandPayoutService.getBalance();
      if (response.success) {
        setBalance(response.data);
        setBalanceError(null);
      }
    } catch (error) {
      setBalance(null);
      setBalanceError(
        error.response?.data?.code === 'STRIPE_NOT_CONFIGURED'
          ? 'Stripe is not configured yet.'
          : 'Unable to load platform balance.'
      );
    }
  }, []);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const stats = useMemo(() => {
    const acc = { pending: 0, approved: 0, paid: 0, failed: 0 };
    for (const p of payouts) {
      if (p.status === 'pending_review') acc.pending += p.amount || 0;
      else if (p.status === 'approved' || p.status === 'processing') acc.approved += p.amount || 0;
      else if (p.status === 'paid') acc.paid += p.amount || 0;
      else if (p.status === 'failed') acc.failed += p.amount || 0;
    }
    return acc;
  }, [payouts]);

  const handleApprove = async (record) => {
    setActioningId(record._id);
    try {
      const response = await OnDemandPayoutService.approvePayout(record._id);
      if (response.success) {
        message.success('Payout approved');
        fetchPayouts();
        fetchBalance();
      } else if (response.message) {
        message.error(response.message);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to approve payout');
    } finally {
      setActioningId(null);
    }
  };

  const handleSend = (record) => {
    const worker = record.hcw
      ? `${record.hcw.firstName} ${record.hcw.lastName}`
      : 'this worker';
    Modal.confirm({
      title: `Are you sure you want to send ${money(record.amount)}?`,
      content: `This transfers ${money(record.amount)} to ${worker} via Stripe. This cannot be undone.`,
      okText: `Send ${money(record.amount)}`,
      okButtonProps: { danger: true },
      onOk: async () => {
        setActioningId(record._id);
        try {
          const response = await OnDemandPayoutService.sendPayout(record._id);
          if (response.success) {
            message.success('Payout sent');
            fetchPayouts();
            fetchBalance();
          } else {
            message.error(response.message || 'Failed to send payout');
          }
        } catch (error) {
          message.error(error.response?.data?.message || 'Failed to send payout');
        } finally {
          setActioningId(null);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Worker',
      dataIndex: 'hcw',
      key: 'hcw',
      render: (hcw) =>
        hcw ? (
          <div>
            <div style={{ fontWeight: 600 }}>{`${hcw.firstName} ${hcw.lastName}`}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{hcw.email}</div>
          </div>
        ) : (
          '—'
        ),
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      render: (event) => event?.name || '—',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => <span style={{ fontWeight: 600 }}>{money(amount)}</span>,
    },
    {
      title: 'Payouts Enabled',
      key: 'ready',
      render: (_, record) => {
        const ready = record.hcw?.payoutAccount?.payoutsEnabled;
        return (
          <StatusTag color={ready ? 'green' : 'default'}>
            {ready ? 'Ready' : 'Not onboarded'}
          </StatusTag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const meta = STATUS_META[status] || { label: status, color: 'default' };
        return <StatusTag color={meta.color}>{meta.label}</StatusTag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 220,
      render: (_, record) => {
        const busy = actioningId === record._id;
        const ready = record.hcw?.payoutAccount?.payoutsEnabled;
        if (record.status === 'pending_review') {
          return (
            <ActionButton
              type="primary"
              size="small"
              loading={busy}
              onClick={() => handleApprove(record)}
            >
              Approve
            </ActionButton>
          );
        }
        if (record.status === 'approved' || record.status === 'failed') {
          const btn = (
            <ActionButton
              type="primary"
              size="small"
              danger={record.status === 'failed'}
              loading={busy}
              disabled={!ready}
              onClick={() => handleSend(record)}
            >
              {record.status === 'failed' ? 'Retry Send' : 'Send Payout'}
            </ActionButton>
          );
          return ready ? (
            btn
          ) : (
            <Tooltip title="Worker must finish Stripe payout onboarding first">
              <span>{btn}</span>
            </Tooltip>
          );
        }
        if (record.status === 'paid') {
          return (
            <Space size={4} style={{ color: '#28A745' }}>
              <FontAwesomeIcon icon={faCircleCheck} />
              <span>Paid</span>
            </Space>
          );
        }
        return '—';
      },
    },
  ];

  return (
    <PageContainer>
      {balanceError && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={balanceError}
        />
      )}
      {balance && (
        <Alert
          type={balance.shortfall > 0 ? 'warning' : 'success'}
          showIcon
          style={{ marginBottom: 16 }}
          message={
            balance.shortfall > 0
              ? `Top up needed: ${money(balance.shortfall)} short to cover approved payouts`
              : 'Platform balance covers all approved payouts'
          }
          description={
            <span>
              Available <strong>{money(balance.availableBalance)}</strong>
              {balance.pendingBalance > 0 && (
                <> · Pending settlement {money(balance.pendingBalance)}</>
              )}{' '}
              · Approved owed <strong>{money(balance.approvedOwed)}</strong>
              {` (${balance.approvedCount})`}
            </span>
          }
        />
      )}

      <StatsRow className="header-animate-in">
        <StatCard $bgColor="#FFF7E6" $iconColor="#FA8C16">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{money(stats.pending)}</div>
            <div className="stat-label">In review</div>
          </div>
        </StatCard>
        <StatCard $bgColor="#E6F4FF" $iconColor="#1890FF">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faPaperPlane} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{money(stats.approved)}</div>
            <div className="stat-label">Approved / processing</div>
          </div>
        </StatCard>
        <StatCard $bgColor="#F6FFED" $iconColor="#28A745">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faMoneyBillWave} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{money(stats.paid)}</div>
            <div className="stat-label">Paid out</div>
          </div>
        </StatCard>
        <StatCard $bgColor="#FFF1F0" $iconColor="#F5222D">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{money(stats.failed)}</div>
            <div className="stat-label">Failed</div>
          </div>
        </StatCard>
      </StatsRow>

      <TopBar>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_FILTERS}
          style={{ width: 200 }}
        />
        <ActionButton onClick={() => { fetchPayouts(); fetchBalance(); }}>Refresh</ActionButton>
      </TopBar>

      <StyledTable
        className="table-animate-in"
        rowKey="_id"
        columns={columns}
        dataSource={payouts}
        loading={loading}
        scroll={{ x: 900 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{
          emptyText: (
            <EmptyState>
              <div className="icon">
                <FontAwesomeIcon icon={faMoneyBillWave} />
              </div>
              <div className="title">No payouts</div>
              <div className="description">
                Payouts appear here after workers complete shifts.
              </div>
            </EmptyState>
          ),
        }}
      />
    </PageContainer>
  );
};

export default PayoutsPage;
